/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterViewInit,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { fromEvent$ } from '@gh/rx';
import { switchMap$, takeUntil$, tap$ } from '@gh/rx/operators';
import { Subject } from 'rxjs';

export abstract class SplitPaneComponent implements AfterViewInit, OnChanges, OnDestroy {

  @ViewChild('primaryComponent', { static: false }) primaryComponent: ElementRef;
  @ViewChild('secondaryComponent', { static: false }) secondaryComponent: ElementRef;

  @Input('primary-component-initialratio') initialRatio: number = 0.5;
  @Input('primary-component-minsize') primaryMinSize: number = 0;
  @Input('secondary-component-minsize') secondaryMinSize: number = 0;
  @Input('separator-thickness') separatorThickness: number = 7;
  @Input('primary-component-toggled-off') primaryToggledOff: boolean = false;
  @Input('secondary-component-toggled-off') secondaryToggledOff: boolean = false;
  @Input('local-storage-key') localStorageKey?: string;
  @Output('on-change') notifySizeDidChange: EventEmitter<any> = new EventEmitter<any>();
  @Output('on-begin-resizing') notifyBeginResizing: EventEmitter<any> = new EventEmitter<any>();
  @Output('on-ended-resizing') notifyEndedResizing: EventEmitter<any> = new EventEmitter<any>();

  primarySizeBeforeTogglingOff: number;
  dividerSize: number = 8.0;
  expanded: boolean;
  isResizing: boolean = false;
  resizing$$ = new Subject<boolean>();

  constructor(private element: ElementRef) {

  }

  // tslint:disable-next-line:contextual-lifecycle
  ngAfterViewInit(): void {
    this.checkBothToggledOff();

    if (!this.primaryToggledOff && !this.secondaryToggledOff) {
      let ratio: number = this.initialRatio;
      if (this.localStorageKey) {
        const ratioStr = localStorage.getItem(this.localStorageKey);
        if (ratioStr) {
          ratio = JSON.parse(ratioStr);
        }
      }

      const size = ratio * this.getTotalSize();
      this.applySizeChange(size);
    }

    this.resizing$$.pipe(
      switchMap$((resizing) => resizing ?
        fromEvent$(this.element.nativeElement, 'mousemove').pipe(
          takeUntil$(fromEvent$(document, 'mouseup').pipe(tap$((event: MouseEvent) => this.onMouseUp(event)))))
        : []))
      .subscribe((event: MouseEvent) => this.onMouseMove(event));
  }

  ngOnDestroy(): void {
    this.resizing$$.next(false);
    this.resizing$$.complete();
  }

  // tslint:disable-next-line:contextual-lifecycle
  ngOnChanges(changes: SimpleChanges): void {
    this.checkBothToggledOff();

    if (changes['primaryToggledOff']) {
      if (changes['primaryToggledOff'].currentValue === true) {
        this.primarySizeBeforeTogglingOff = this.getPrimarySize();
        this.applySizeChange(0);
      } else {
        this.applySizeChange(this.primarySizeBeforeTogglingOff);
      }
    } else if (changes['secondaryToggledOff']) {
      if (changes['secondaryToggledOff'].currentValue === true) {
        this.primarySizeBeforeTogglingOff = this.getPrimarySize();
        this.applySizeChange(this.getTotalSize());
      } else {
        this.applySizeChange(this.primarySizeBeforeTogglingOff);
      }
    }
  }

  getTotalSize(): number {
    throw new Error("SplitPaneComponent shouldn't be instantiated. Override this method.");
  }

  getPrimarySize(): number {
    throw new Error("SplitPaneComponent shouldn't be instantiated. Override this method.");
  }

  getSecondarySize(): number {
    throw new Error("SplitPaneComponent shouldn't be instantiated. Override this method.");
  }

  dividerPosition(size: number, isInitial?: boolean): void {
    throw new Error("SplitPaneComponent shouldn't be instantiated. Override this method.");
  }

  getAvailableSize(): number {
    return this.getTotalSize() - this.dividerSize;
  }

  applySizeChange(size: number): void {
    let primarySize = this.checkValidBounds(
      size, this.primaryMinSize,
      this.getAvailableSize() - this.secondaryMinSize);

    if (this.primaryToggledOff) {
      primarySize = 0;
    } else if (this.secondaryToggledOff) {
      primarySize = this.getTotalSize();
    }
    this.dividerPosition(primarySize);

    const primaryRatio = this.getPrimarySize() / this.getTotalSize();
    // tslint:disable-next-line:no-magic-numbers
    this.notifySizeDidChange.emit({ 'primary': primaryRatio > 0.98 ? 1 : primaryRatio });
  }

  notifyWillChangeSize(resizing: boolean): void {
    this.resizing$$.next(resizing);
    this.notifyBeginResizing.emit();
  }

  checkValidBounds(newSize: number, minSize: number, maxSize: number): number {
    return newSize >= minSize
      ? (newSize <= maxSize)
        ? newSize
        : maxSize
      : minSize;
  }

  checkBothToggledOff(): void {
    // We do not allow both the primary and secondary content to be toggled off
    // at the same time, because then it would be very confusing.
    if (this.primaryToggledOff && this.secondaryToggledOff) {
      throw new Error('You cannot toggle off both the primary and secondary component');
    }
  }

  stopResizing(): void {
    this.primaryComponent.nativeElement.style.cursor = 'auto';
    this.secondaryComponent.nativeElement.style.cursor = 'auto';

    if (this.localStorageKey) {
      const ratio = this.getPrimarySize() / (this.getTotalSize());
      localStorage.setItem(this.localStorageKey, JSON.stringify(ratio));
    }

    this.notifyEndedResizing.emit();
  }

  onMouseUp(event: MouseEvent): void {
    this.stopResizing();
    event.preventDefault();
  }

  protected abstract onMouseMove(event: MouseEvent): void;
}
